/**
 * Pokémon Evolution & Theme Mapping System
 *
 * Defines evolution families and theme groupings for bundle/recommendation logic.
 * Data is kept separate from logic for easy expansion.
 */

export type ThemeType =
  | "evolution_family"
  | "legendary_group"
  | "mythical_group"
  | "special_group";

export interface EvolutionFamily {
  /** Stable unique identifier – snake_case */
  familyId: string;
  /** Human-readable display label */
  label: string;
  /** Exact Pokémon names (canonical casing) */
  pokemon: string[];
  /**
   * The "core" line for hasFullLine checks.
   * Defaults to `pokemon` if omitted (use for branching lines like Eevee
   * where you only need to own Eevee + any one evolution).
   */
  coreLine?: string[];
  /** Optional grouping aliases shown in search / tags */
  aliases?: string[];
  /** Higher = more recognisable / commercially valuable */
  priority?: number;
  themeType: ThemeType;
}

/**
 * Normalises a Pokémon name for comparison:
 * - lower-case
 * - strip punctuation (handles "Mr. Mime", "Farfetch'd", "Ho-Oh", "Mime Jr.")
 * - collapse whitespace
 */
export function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export const EVOLUTION_FAMILIES: EvolutionFamily[] = [
  // ── Kanto Starters ───────────────────────────────────────────────────────
  {
    familyId: "bulbasaur_line",
    label: "Bulbasaur Evolution Line",
    pokemon: ["Bulbasaur", "Ivysaur", "Venusaur"],
    aliases: ["Grass Starter", "Frog Starter"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "charmander_line",
    label: "Charizard Evolution Line",
    pokemon: ["Charmander", "Charmeleon", "Charizard"],
    aliases: ["Fire Starter", "Charizard line"],
    priority: 10,
    themeType: "evolution_family",
  },
  {
    familyId: "squirtle_line",
    label: "Blastoise Evolution Line",
    pokemon: ["Squirtle", "Wartortle", "Blastoise"],
    aliases: ["Water Starter", "Turtle line"],
    priority: 8,
    themeType: "evolution_family",
  },

  // ── Iconic Kanto ─────────────────────────────────────────────────────────
  {
    familyId: "pikachu_line",
    label: "Pikachu Evolution Line",
    pokemon: ["Pichu", "Pikachu", "Raichu"],
    coreLine: ["Pikachu", "Raichu"],
    aliases: ["Electric Mouse", "Pika"],
    priority: 10,
    themeType: "evolution_family",
  },
  {
    familyId: "eevee_line",
    label: "Eeveelution Family",
    pokemon: [
      "Eevee",
      "Vaporeon",
      "Jolteon",
      "Flareon",
      "Espeon",
      "Umbreon",
      "Leafeon",
      "Glaceon",
      "Sylveon",
    ],
    coreLine: ["Eevee"],
    aliases: ["Eeveelutions", "Eevee evolutions"],
    priority: 9,
    themeType: "special_group",
  },
  {
    familyId: "gastly_line",
    label: "Gengar Evolution Line",
    pokemon: ["Gastly", "Haunter", "Gengar"],
    aliases: ["Ghost line", "Gengar line"],
    priority: 9,
    themeType: "evolution_family",
  },
  {
    familyId: "magikarp_line",
    label: "Gyarados Evolution Line",
    pokemon: ["Magikarp", "Gyarados"],
    aliases: ["Fish line", "Gyarados line"],
    priority: 9,
    themeType: "evolution_family",
  },
  {
    familyId: "abra_line",
    label: "Alakazam Evolution Line",
    pokemon: ["Abra", "Kadabra", "Alakazam"],
    aliases: ["Psychic line", "Spoon line"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "machop_line",
    label: "Machamp Evolution Line",
    pokemon: ["Machop", "Machoke", "Machamp"],
    aliases: ["Fighting line", "Muscle line"],
    priority: 6,
    themeType: "evolution_family",
  },
  {
    familyId: "dratini_line",
    label: "Dragonite Evolution Line",
    pokemon: ["Dratini", "Dragonair", "Dragonite"],
    aliases: ["Dragon line", "Dragonite line"],
    priority: 9,
    themeType: "evolution_family",
  },

  // ── Kanto Legendaries ────────────────────────────────────────────────────
  {
    familyId: "legendary_birds",
    label: "Legendary Birds",
    pokemon: ["Articuno", "Zapdos", "Moltres"],
    aliases: ["Legendary Birds", "Kanto Birds", "Trio Birds"],
    priority: 8,
    themeType: "legendary_group",
  },
  {
    familyId: "mew_line",
    label: "Mew & Mewtwo",
    pokemon: ["Mew", "Mewtwo"],
    aliases: ["Psychic Legends", "Original Legends"],
    priority: 10,
    themeType: "legendary_group",
  },

  // ── Johto ────────────────────────────────────────────────────────────────
  {
    familyId: "cyndaquil_line",
    label: "Typhlosion Evolution Line",
    pokemon: ["Cyndaquil", "Quilava", "Typhlosion"],
    aliases: ["Johto Fire Starter"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "totodile_line",
    label: "Feraligatr Evolution Line",
    pokemon: ["Totodile", "Croconaw", "Feraligatr"],
    aliases: ["Johto Water Starter"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "legendary_beasts",
    label: "Legendary Beasts",
    pokemon: ["Raikou", "Entei", "Suicune"],
    aliases: ["Legendary Beasts", "Johto Dogs", "Johto Cats"],
    priority: 8,
    themeType: "legendary_group",
  },
  {
    familyId: "lugia_ho_oh",
    label: "Tower Duo",
    pokemon: ["Lugia", "Ho-Oh"],
    aliases: ["Tower Duo", "Johto Birds", "Johto Legends"],
    priority: 9,
    themeType: "legendary_group",
  },

  // ── Hoenn ────────────────────────────────────────────────────────────────
  {
    familyId: "ralts_line",
    label: "Gardevoir / Gallade Evolution Line",
    pokemon: ["Ralts", "Kirlia", "Gardevoir", "Gallade"],
    coreLine: ["Ralts", "Kirlia"],
    aliases: ["Gardevoir line", "Gallade line", "Psychic line"],
    priority: 8,
    themeType: "evolution_family",
  },
  {
    familyId: "weather_trio",
    label: "Weather Trio",
    pokemon: ["Groudon", "Kyogre", "Rayquaza"],
    aliases: ["Weather Trio", "Hoenn Legends", "Sky Trio"],
    priority: 9,
    themeType: "legendary_group",
  },

  // ── Sinnoh ───────────────────────────────────────────────────────────────
  {
    familyId: "riolu_line",
    label: "Lucario Evolution Line",
    pokemon: ["Riolu", "Lucario"],
    aliases: ["Aura line", "Lucario line"],
    priority: 9,
    themeType: "evolution_family",
  },
  {
    familyId: "creation_trio",
    label: "Creation Trio",
    pokemon: ["Dialga", "Palkia", "Giratina"],
    aliases: ["Creation Trio", "Sinnoh Legends", "Dragon Legends"],
    priority: 9,
    themeType: "legendary_group",
  },
  {
    familyId: "lake_guardians",
    label: "Lake Guardians",
    pokemon: ["Uxie", "Mesprit", "Azelf"],
    aliases: ["Lake Trio", "Lake Guardians"],
    priority: 6,
    themeType: "legendary_group",
  },

  // ── Unova ────────────────────────────────────────────────────────────────
  {
    familyId: "reshiram_zekrom",
    label: "Tao Trio",
    pokemon: ["Reshiram", "Zekrom", "Kyurem"],
    aliases: ["Tao Trio", "Unova Legends", "Dragon Trio"],
    priority: 8,
    themeType: "legendary_group",
  },

  // ── Kalos ────────────────────────────────────────────────────────────────
  {
    familyId: "xerneas_yveltal",
    label: "Aura Trio",
    pokemon: ["Xerneas", "Yveltal", "Zygarde"],
    aliases: ["Aura Trio", "Kalos Legends"],
    priority: 7,
    themeType: "legendary_group",
  },

  // ── Special Groupings ────────────────────────────────────────────────────
  {
    familyId: "kanto_starters",
    label: "Kanto Starter Set",
    pokemon: [
      "Bulbasaur",
      "Ivysaur",
      "Venusaur",
      "Charmander",
      "Charmeleon",
      "Charizard",
      "Squirtle",
      "Wartortle",
      "Blastoise",
    ],
    coreLine: ["Bulbasaur", "Charmander", "Squirtle"],
    aliases: ["Gen 1 Starters", "Original Starters"],
    priority: 10,
    themeType: "special_group",
  },
  {
    familyId: "baby_pokemon",
    label: "Baby Pokémon",
    pokemon: [
      "Pichu",
      "Cleffa",
      "Igglybuff",
      "Togepi",
      "Tyrogue",
      "Smoochum",
      "Elekid",
      "Magby",
      "Azurill",
      "Wynaut",
      "Budew",
      "Chingling",
      "Bonsly",
      "Mime Jr",
      "Happiny",
      "Munchlax",
      "Riolu",
      "Mantyke",
    ],
    aliases: ["Baby Pokémon", "Pre-evolutions"],
    priority: 4,
    themeType: "special_group",
  },
  {
    familyId: "mythical_set",
    label: "Mythical Pokémon",
    pokemon: [
      "Mew",
      "Celebi",
      "Jirachi",
      "Deoxys",
      "Manaphy",
      "Phione",
      "Darkrai",
      "Shaymin",
      "Arceus",
      "Victini",
      "Keldeo",
      "Meloetta",
      "Genesect",
      "Diancie",
      "Hoopa",
      "Volcanion",
    ],
    aliases: ["Mythicals", "Event Pokémon"],
    priority: 7,
    themeType: "mythical_group",
  },

  // ── Pseudo-legendary lines ──────────────────────────────────────────────
  {
    familyId: "larvitar_line",
    label: "Tyranitar Evolution Line",
    pokemon: ["Larvitar", "Pupitar", "Tyranitar"],
    aliases: ["Pseudo-legendary Rock", "Tyranitar line"],
    priority: 8,
    themeType: "evolution_family",
  },
  {
    familyId: "bagon_line",
    label: "Salamence Evolution Line",
    pokemon: ["Bagon", "Shelgon", "Salamence"],
    aliases: ["Dragon pseudo-legendary", "Salamence line"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "beldum_line",
    label: "Metagross Evolution Line",
    pokemon: ["Beldum", "Metang", "Metagross"],
    aliases: ["Steel pseudo-legendary", "Metagross line"],
    priority: 7,
    themeType: "evolution_family",
  },
  {
    familyId: "gible_line",
    label: "Garchomp Evolution Line",
    pokemon: ["Gible", "Gabite", "Garchomp"],
    aliases: ["Shark Dragon", "Garchomp line"],
    priority: 8,
    themeType: "evolution_family",
  },
];

/**
 * Maps normalised Pokémon name → array of family IDs it belongs to.
 * A Pokémon can appear in multiple families (e.g. Pichu in pikachu_line
 * AND baby_pokemon).
 */
export const NAME_TO_FAMILY_IDS: Map<string, string[]> = new Map();

for (const family of EVOLUTION_FAMILIES) {
  for (const name of family.pokemon) {
    const key = normalizeName(name);
    const existing = NAME_TO_FAMILY_IDS.get(key) ?? [];
    existing.push(family.familyId);
    NAME_TO_FAMILY_IDS.set(key, existing);
  }
}

/** Fast familyId → EvolutionFamily lookup */
export const FAMILY_BY_ID: Map<string, EvolutionFamily> = new Map(
  EVOLUTION_FAMILIES.map((f) => [f.familyId, f]),
);
